import { useCallback, useEffect, useRef, useState } from 'react';
import uniqBy from 'lodash/uniqBy';
import { toast } from 'sonner';
import { apiErrorMessage, parseApiError } from '../utils/apiError';
import baseAxios from '../utils/request';

export function usePostRequest(options = {}) {
  return useRequest({ method: 'POST', ...options });
}

export function usePutRequest(options = {}) {
  return useRequest({ method: 'PUT', ...options });
}

export function useGetRequest(options = {}) {
  return useRequest({ method: 'GET', ...options });
}

export function useDeleteRequest(options = {}) {
  return useRequest({ method: 'DELETE', ...options });
}

export function usePatchRequest(options = {}) {
  return useRequest({ method: 'PATCH', ...options });
}

export function useRequest(options = {}) {
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});

  async function request(overrideOptions = {}, sync = false) {
    setLoading(true);

    const mergedOptions = {
      showError: true,
      ...options,
      ...overrideOptions,
    };

    try {
      // docs-api on this origin, with the BFF session cookie (request.js).
      const { data } = await baseAxios({ ...options, ...overrideOptions });
      if (!sync) setResponse(data);
      return { response: data, success: true };
    } catch (e) {
      const errResponse = e.response;
      // Structured envelope, parsed once for everyone: {code, message,
      // params, status, requestId}. Display text resolves from the code via
      // errors.json; `errorMessages: {code: text|fn}` overrides per call.
      const errorInfo = parseApiError(e);
      setError(errResponse);

      // A 401 is the session's: @zehnmindai/embed's interceptor already read
      // it again and told the SessionProvider, which signs the person in.
      if (errResponse?.status !== 401 && mergedOptions.showError) {
        toast.error(apiErrorMessage(errorInfo, mergedOptions.errorMessages));
      }

      return { error: errResponse, errorInfo, success: false };
    } finally {
      if (!sync) setLoading(false);
    }
  }

  return {
    loading,
    request,
    error,
    response,
    setResponse,
    setError,
    setLoading,
  };
}

export function useLoad(options, dependencies = []) {
  const request = useRequest({ method: 'GET', ...options });

  useEffect(() => {
    request.request();

    return () => {
      request.setResponse([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies]);

  return request;
}

export function useInfiniteScroll(options, condition = true, dependencies = []) {
  const [page, setPage] = useState(1);
  const items = useGetRequest({
    ...options,
    params: { ...options.params, page },
  });
  const [hasMore, setHasMore] = useState(false);
  const observer = useRef();

  useEffect(() => {
    if (condition) loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, ...dependencies]);

  async function loadItems() {
    const { response } = await items.request();
    const oldItems = items.response ? items.response.items : [];
    const newItems = response ? response.items : [];

    if (!response) return;

    items.setResponse({
      ...response,
      count: response ? response.count : 0,
      items: uniqBy([...oldItems, ...newItems], 'uuid'),
    });

    setHasMore(page < response.pages);
  }

  function reset() {
    items.setResponse({ count: 0, items: [] });
    setPage(1);
  }

  async function reload() {
    reset();
    await items.request({ params: { ...options.params, page: 1 } });
  }

  const ref = useCallback(
    (node) => {
      if (items.loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage(page + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasMore, items.loading, page]
  );

  return {
    ref,
    ...items,
    hasMore,
    reload,
    reset,
  };
}
